import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Check, X, Loader2, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiGet, apiPost } from '../utils/api';

function AcceptSeatInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser } = useAuth();
  const { theme } = useTheme();
  const d = theme === 'dark';

  const { data: invitation, isLoading, error } = useQuery({
    queryKey: ['seat-invitation', token],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'}/seat-invitations/${token}`);
      if (!res.ok) throw new Error('Invitation not found');
      return res.json();
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`/seat-invitations/${token}/accept`, {});
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }
      return res.json();
    },
    onSuccess: async (data) => {
      await refreshUser();
      navigate('/dashboard', { replace: true });
    },
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      const res = await apiPost(`/seat-invitations/${token}/decline`, {});
      return res.json();
    },
    onSuccess: () => navigate('/', { replace: true }),
  });

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${d ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${d ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <div className="text-center">
          <X size={48} className="mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-dm-serif mb-2">Invitation Not Found</h1>
          <p className="text-gray-500 font-rasa mb-4">This invitation may have expired or been revoked.</p>
          <Link to="/" className="text-blue-500 font-rasa hover:underline">Go to homepage</Link>
        </div>
      </div>
    );
  }

  const isExpired = invitation.expired || invitation.status !== 'pending';

  return (
    <div className={`min-h-screen flex items-center justify-center ${d ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      <div className={`max-w-md w-full mx-4 p-8 rounded-xl shadow-lg ${d ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center mb-6">
          <div className={`inline-flex p-3 rounded-full mb-4 ${d ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <Users size={24} className="text-blue-500" />
          </div>
          <h1 className="text-2xl font-dm-serif mb-2">Seat Invitation</h1>
          <p className={`font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            <strong>{invitation.inviter_name}</strong> has shared their <strong>{invitation.plan_name}</strong> subscription with you.
          </p>
        </div>

        {isExpired ? (
          <div className={`p-4 rounded-lg text-center ${d ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className="text-gray-500 font-rasa">
              This invitation has {invitation.status === 'pending' ? 'expired' : `been ${invitation.status}`}.
            </p>
          </div>
        ) : !isAuthenticated ? (
          <div className="space-y-3">
            <p className={`text-sm font-rasa text-center ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              You need a JPro account to accept this invitation.
            </p>
            <div className="flex gap-3">
              <Link
                to={`/login?redirect=/seat-invitations/${token}`}
                className="flex-1 py-2.5 text-center rounded-lg font-rasa text-sm border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Log in
              </Link>
              <Link
                to={`/signup?redirect=/seat-invitations/${token}`}
                className="flex-1 py-2.5 text-center rounded-lg font-rasa text-sm bg-blue-500 text-white hover:bg-blue-600"
              >
                Sign up
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {acceptMutation.isError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm font-rasa">
                {acceptMutation.error?.message}
              </div>
            )}
            <p className={`text-sm font-rasa text-center ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              Accepting gives you access to all features in the {invitation.plan_name} plan.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => declineMutation.mutate()}
                disabled={declineMutation.isPending}
                className={`flex-1 py-2.5 rounded-lg font-rasa text-sm border transition-colors ${d ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                Decline
              </button>
              <button
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
                className="flex-1 py-2.5 rounded-lg font-rasa text-sm bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {acceptMutation.isPending ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                Accept
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptSeatInvitation;
